import { ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { createUploadLink } from 'apollo-upload-client'
import { sha256 } from 'crypto-hash'
import pLimit from 'p-limit'

import { REALMS } from 'lib/keycloak/keycloak.consts'
import { authService } from 'services/auth'
import datadog from 'utils/datadog'
import type { TDatadogErrorLog } from 'utils/datadog/types'
import getCurrentWorkspace from 'utils/getCurrentWorkspace'
import getEnvFromLocation from 'utils/getEnvFromLocation'

import { FALLBACK_ERROR_LOG, IGNORED_GRAPHQL_ERROR_MESSAGES } from './consts'

const authLink = setContext(async (_, { headers, skipWorkspaceHeader }) => {
  // Operations that run before a workspace is selected (auth bootstrap `getMe`) pass
  // `context: { skipWorkspaceHeader: true }`: the server's `@authorized` directive on `me`
  // rejects the whole query when the workspace header points at a workspace the user can't
  // access, and `getCurrentWorkspace()` may still resolve a stale/foreign `panelId` here.
  const currentWorkspace = skipWorkspaceHeader ? null : getCurrentWorkspace()

  // `realm` and the workspace header don't depend on the token, so they belong on every
  // request — including the failure path below.
  const baseHeaders = {
    ...headers,
    ...(currentWorkspace && { 'x-insights-workspace': `${currentWorkspace}` }),
    realm: REALMS[getEnvFromLocation()],
  }

  try {
    const accessToken = await authService.getAccessToken()

    return {
      headers: {
        ...baseHeaders,
        ...(accessToken && { authorization: `${accessToken}` }),
      },
    }
  } catch (error: unknown) {
    // A token-refresh failure must not drop the request's context: returning `undefined` here
    // would strip every header (incl. `realm`), malforming the request instead of letting it
    // fail cleanly with a 401. Send the auth-free headers and route the log through Datadog
    // (the project standard) rather than console.error.
    datadog.dispatchGenericErrorLog({
      log: { from: 'apollo-auth-link', name: 'auth_link_failed', message: 'authService.getAccessToken() rejected' },
      error: error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'non-Error rejection'),
    })
    return { headers: baseHeaders }
  }
})

// Central error reporting: every operation error is logged to Datadog from
// here, with the real operationName and variables. Call sites keep their
// curated `from`/`name`/`message` by passing a TDatadogErrorLog through the
// operation context: `context: { errorLog }`.
// On Apollo Client 4 this becomes `new ErrorLink(({ error, operation }) => ...)`
// with the same design.
const ErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const relevantGraphQLErrors = (graphQLErrors ?? []).filter(
    graphQLError => !IGNORED_GRAPHQL_ERROR_MESSAGES.has(graphQLError?.message)
  )

  if (!relevantGraphQLErrors.length && !networkError) return

  const { errorLog } = operation.getContext() as { errorLog?: TDatadogErrorLog }

  datadog.dispatchOperationErrorLog({
    log: errorLog ?? { ...FALLBACK_ERROR_LOG, name: `GraphQL Error: ${operation.operationName}` },
    operation: {
      name: operation.operationName,
      variables: operation.variables,
    },
    graphQLErrors: relevantGraphQLErrors,
    networkError,
  })
})

const limit = pLimit(5)

function fetchWithCustomURI(_, options) {
  let qs = ''
  if (process.env.NODE_ENV === 'development') {
    try {
      const body = JSON.parse(options.body)
      qs = `?operationName=${body?.operationName}`
    } catch (ignoreError) {
      // Ignore
    }
  }

  return limit(() => fetch(`/graphql${qs}`, options))
}

const httpLink = createUploadLink({ fetch: fetchWithCustomURI })

const links = [createPersistedQueryLink({ sha256 }), authLink, ErrorLink, httpLink]

export const link = ApolloLink.from(links)
